import { AvailableOrganizer } from 'modules/organizers/domain/interfaces/available-organizer';
import { ListAvailableOrganizersArgs, useListAvailableOrganizersQuery } from 'modules/organizers/store/available-organizers-api';
import { useAuth } from 'modules/auth/ui/hooks/use-auth';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOffsetPagination } from 'shared/hooks/use-offset-pagination';

/** Не отправляем на бэк слишком короткий поисковый запрос */
const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 500;
const TOURNAMENTS_PER_PAGE = 6;
const SEARCH_PARAM = 'filter';

const filtersConfig = {
  filter: { key: SEARCH_PARAM, defaultValue: '' as string },
};

const mapFiltersToArgs = (filters: { filter: string }) => {
  const namePart = filters.filter.trim();
  return { namePart: namePart.length >= MIN_SEARCH_LENGTH ? namePart : undefined };
};

export function useAvailableOrganizers() {
  const { user, isChecking } = useAuth();

  // Черновик поиска нужен до вызова пагинации (он идёт туда как `draftFilters`),
  // поэтому начальное значение читается из URL напрямую — тем же `useSearchParams`,
  // на котором построен `useQueryParams` внутри пагинации. Так `?filter=` переживает
  // перезагрузку страницы.
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get(SEARCH_PARAM) ?? '');

  const { items, state, isError, page, totalPages, filters, setPage, setFilters } = useOffsetPagination<
    AvailableOrganizer,
    ListAvailableOrganizersArgs,
    typeof filtersConfig
  >({
    useQueryHook: useListAvailableOrganizersQuery,
    mapFiltersToArgs,
    filtersConfig,
    // Пока проверяется авторизация, запрос пропускается (`skip` ниже). Для UI это
    // такое же ожидание, как запрос в полёте, иначе экран отрапортует «ничего нет».
    isPending: isChecking,
    // Загорается индикатор на первом символе, а не после дебаунса.
    draftFilters: { filter: search },
    perPageStorageKey: 'availableTournamentsPerPage',
    defaultPerPage: TOURNAMENTS_PER_PAGE,
    queryOptions: () => ({ skip: !user?.id }),
  });

  // Инпут обновляется мгновенно, в URL (и, следовательно, в запрос) значение
  // уезжает с задержкой. Дебаунс на эффекте, а не на debounce(): setFilters
  // пересоздаётся при каждом изменении searchParams и сбрасывал бы таймер,
  // плюс эффект сам чистит таймер при размонтировании.
  const setFiltersRef = useRef(setFilters);
  setFiltersRef.current = setFilters;

  useEffect(() => {
    if (search === filters.filter) {
      return;
    }

    // Пустая строка → undefined, чтобы параметр вычищался из URL, а не висел как ?filter=
    const timer = setTimeout(() => setFiltersRef.current({ filter: search || undefined }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, filters.filter]);

  return {
    data: items,
    state,
    isError,
    /** Введено ли что-то в поиск — от этого зависит текст пустого состояния. */
    hasSearch: search.trim().length > 0,
    search,
    setSearch,
    page,
    pages: totalPages,
    setPage,
  };
}
